import { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Link, Text, VStack } from '@chakra-ui/react';
import { writeConsentCookie, getAcceptedCategories } from './consentCookie';
import { updateGoogleConsentMode } from './consentModeMapping';
import type { ConsentRegion } from './geoDetect';

const COLORS = {
  black: 'primaryDark',
  body: '#444444',
  link: 'orangePrimary',
  buttonBg: 'lightGrey',
  buttonHoverBg: '#E8E8E8',
  buttonHoverText: '#999999',
  toggleOn: 'primaryDark',
  toggleOff: '#0000001A',
  categoryBg: '#00000005',
};

const bannerButtonStyle = {
  w: '100%',
  h: 'auto',
  p: '8px',
  borderRadius: '8px',
  border: 'none',
  bg: COLORS.buttonBg,
  color: COLORS.body,
  fontSize: '14px',
  lineHeight: '1em',
  fontWeight: 400,
  fontFamily: 'Inter, "Inter Placeholder", sans-serif',
  fontStyle: 'normal',
  letterSpacing: '0.02em',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease, color 0.3s ease',
  _hover: { bg: COLORS.buttonHoverBg, color: COLORS.buttonHoverText },
};

interface CategoryConfig {
  key: string;
  label: string;
  description: string;
  readOnly?: boolean;
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: 'essential',
    label: 'Essential',
    description: 'Enables security and basic functionality.',
    readOnly: true,
  },
  {
    key: 'functional',
    label: 'Functional',
    description: 'Enables personalised content and settings.',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Enables tracking of performance.',
  },
  {
    key: 'marketing',
    label: 'Marketing & Advertising',
    description: 'Enables ads personalisation and tracking.',
  },
];

const Toggle = ({
  isOn,
  onToggle,
}: {
  isOn: boolean;
  onToggle: (e: React.MouseEvent) => void;
}) => (
  <Box
    as="button"
    onClick={onToggle}
    w="24px"
    h="14px"
    p="2px"
    bg={isOn ? COLORS.toggleOn : COLORS.toggleOff}
    borderRadius="20px"
    display="flex"
    alignItems="center"
    cursor="pointer"
    flexShrink={0}
    transition="background-color 0.2s"
  >
    <Box
      w="10px"
      h="10px"
      bg="white"
      borderRadius="50%"
      transition="transform 0.2s"
      transform={isOn ? 'translateX(10px)' : 'translateX(0)'}
    />
  </Box>
);

const CategorySection = ({
  category,
  isOn,
  onToggle,
}: {
  category: CategoryConfig;
  isOn: boolean;
  onToggle: () => void;
}) => (
  <Box
    bg={COLORS.categoryBg}
    boxShadow="#00000005 0px 0px 0px 0px inset"
    borderRadius="8px"
    p="12px"
    w="100%"
    cursor="pointer"
    userSelect="none"
    transition="opacity 0.3s ease"
    _hover={{ opacity: 0.5 }}
    onClick={() => !category.readOnly && onToggle()}
  >
    <Flex justify="space-between" align="center" mb={1}>
      <Text
        fontSize="12px"
        fontFamily="Inter SemiBold, Inter, sans-serif"
        color={COLORS.black}
        m={0}
      >
        {category.label}
      </Text>
      {category.readOnly ? (
        <Text fontSize="12px" color={COLORS.black} lineHeight="1">
          Always active
        </Text>
      ) : (
        <Toggle
          isOn={isOn}
          onToggle={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      )}
    </Flex>
    <Text fontSize="12px" lineHeight="1.5" color={COLORS.body} m="8px 0 0">
      {category.description}
    </Text>
  </Box>
);

const ALL_CATEGORY_KEYS = CATEGORIES.map((c) => c.key);

const SIGNAL_CATEGORY_KEYS = ['functional', 'analytics', 'marketing'];

function getInitialToggles(): Record<string, boolean> {
  const accepted = getAcceptedCategories();
  return Object.fromEntries(
    CATEGORIES.map((cat) => [
      cat.key,
      cat.readOnly || accepted.includes(cat.key),
    ])
  );
}

interface CookieConsentBannerProps {
  onClose: () => void;
  initialView?: 'banner' | 'preferences';
  region?: ConsentRegion;
}

export const CookieConsentBanner = ({
  onClose,
  initialView = 'banner',
  region = 'gdpr',
}: CookieConsentBannerProps) => {
  const [view, setView] = useState<'banner' | 'preferences'>(initialView);
  const [toggles, setToggles] = useState(getInitialToggles);
  const [prefsHeight, setPrefsHeight] = useState<number | null>(
    initialView === 'preferences' ? null : 0
  );
  const prefsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (view === 'preferences' && prefsRef.current) {
      const height = prefsRef.current.scrollHeight;
      requestAnimationFrame(() => setPrefsHeight(height));
    } else if (view === 'banner') {
      setPrefsHeight(0);
    }
  }, [view]);

  const acceptAndClose = (categories: string | string[]) => {
    const resolved =
      categories === 'all' ? ALL_CATEGORY_KEYS : (categories as string[]);
    const previous = getAcceptedCategories();
    const hasRevoked = previous.some((cat) => !resolved.includes(cat));
    const hasNewlyGranted = resolved.some(
      (cat) => SIGNAL_CATEGORY_KEYS.includes(cat) && !previous.includes(cat)
    );
    writeConsentCookie(resolved);
    updateGoogleConsentMode(resolved);
    if (hasRevoked || hasNewlyGranted) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  const handleSavePreferences = () => {
    const accepted = Object.entries(toggles)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
    acceptAndClose(accepted);
  };

  const handleToggle = (key: string) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const bannerWrapper = (content: React.ReactNode) => (
    <Box
      position="fixed"
      bottom="5px"
      left={0}
      right={0}
      zIndex={2147483647}
      display="flex"
      justifyContent={{ base: 'center', md: 'flex-end' }}
      p={4}
      pointerEvents="none"
    >
      <Box
        bg="white"
        borderRadius="14px"
        p="20px"
        maxW="360px"
        w="100%"
        boxShadow="0 4px 24px #00000026"
        maxH="calc(-40px + 100vh)"
        overflowY="auto"
        pointerEvents="auto"
      >
        {content}
      </Box>
    </Box>
  );

  if (region === 'other') {
    return bannerWrapper(
      <Flex align="center" gap={6}>
        <Text fontSize="14px" color={COLORS.body} flex={1}>
          We use cookies to keep PassEntry running and to understand how you use
          the platform so we can improve it. We don&apos;t sell your data.
        </Text>
        <Button
          {...bannerButtonStyle}
          color={COLORS.body}
          flexShrink={0}
          w="auto"
          onClick={() => acceptAndClose('all')}
        >
          Okay
        </Button>
      </Flex>
    );
  }

  return bannerWrapper(
    <>
      <Text
        fontSize="14px"
        fontFamily="Inter Bold, Inter, sans-serif"
        color={COLORS.black}
        m="-4px 0 8px"
        p={0}
      >
        Cookie Settings
      </Text>

      <Text
        fontSize="14px"
        color={COLORS.body}
        mb={view === 'preferences' ? 5 : 4}
      >
        We use essential cookies to keep PassEntry running. With your consent,
        we also use analytics cookies to understand how you use the platform and
        improve it. We don&apos;t sell your data. Read our{' '}
        <Link
          href="https://passentry.com/cookie-policy/"
          color={COLORS.link}
          fontWeight={400}
          isExternal
        >
          Cookie Policy
        </Link>
        .
      </Text>

      <Box
        ref={prefsRef}
        overflow="hidden"
        transition="max-height 0.4s ease, opacity 0.3s ease"
        maxH={prefsHeight === null ? 'none' : `${prefsHeight}px`}
        opacity={view === 'preferences' ? 1 : 0}
      >
        {view === 'preferences' && (
          <VStack spacing={2} mb={4}>
            {CATEGORIES.map((cat) => (
              <CategorySection
                key={cat.key}
                category={cat}
                isOn={toggles[cat.key]}
                onToggle={() => handleToggle(cat.key)}
              />
            ))}
          </VStack>
        )}
      </Box>

      {view === 'banner' ? (
        <Flex gap={2.5} w="100%">
          <Button
            {...bannerButtonStyle}
            flex={1}
            onClick={() => acceptAndClose(['essential'])}
          >
            Reject all
          </Button>
          <Button
            {...bannerButtonStyle}
            flex={1}
            onClick={() => setView('preferences')}
          >
            Customise
          </Button>
          <Button
            {...bannerButtonStyle}
            flex={1}
            color={COLORS.black}
            onClick={() => acceptAndClose('all')}
          >
            Accept all
          </Button>
        </Flex>
      ) : (
        <Button {...bannerButtonStyle} onClick={handleSavePreferences}>
          Save Preferences
        </Button>
      )}
    </>
  );
};
